import { useQuery } from '@tanstack/react-query';

import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { PaymentPayout, PaymentPayoutDetail } from '@/types/payment-payout';

type PaginatedResponse<T> = {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export const paymentPayoutKeys = {
  all: ['payment-payouts'] as const,
  list: (page: number, limit: number) => [...paymentPayoutKeys.all, 'list', page, limit] as const,
  detail: (id: string) => [...paymentPayoutKeys.all, 'detail', id] as const,
};

/** `GET /payment-account/payouts` (T3.1, T6.1): lista paginada de repasses. */
export function usePaymentPayouts(
  { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
  options?: { enabled?: boolean },
) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentPayoutKeys.list(page, limit),
    queryFn: async () =>
      (
        await api.get<PaginatedResponse<PaymentPayout>>('/payment-account/payouts', {
          params: { page, limit },
        })
      ).data,
    enabled: isHydrated && options?.enabled !== false,
  });
}

/** `GET /payment-account/payouts/:id` (T3.1, T6.2): as cobranças de um repasse. */
export function usePaymentPayout(id: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentPayoutKeys.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Payout ID is required');
      return (await api.get<PaymentPayoutDetail>(`/payment-account/payouts/${id}`)).data;
    },
    enabled: isHydrated && !!id,
  });
}
